'use client';

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useCallback,
} from 'react';
import { useRouter } from 'next/navigation';

import { SubscriptionData } from '@/models/User';

interface UserProfile {
  id: string;
  email: string;
  username: string;
  role: 'admin' | 'user';
  subscription?: SubscriptionData;
  twofa_enabled: boolean;
  profile_image?: string; // GridFS file ID for profile image
  created_at: string;
}

interface AuthContextType {
  user: UserProfile | null;
  userProfile: UserProfile | null;
  session: { access_token: string; refresh_token: string } | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; message: string }>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
  refreshUserProfile: () => Promise<void>;
  checkAuth: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [session, setSession] = useState<{ access_token: string; refresh_token: string } | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const router = useRouter();

  const isAuthenticated = !!user && !!session;

  // Initialize authentication state from localStorage
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Only access localStorage on the client side
        if (typeof window !== 'undefined') {
          const storedToken = localStorage.getItem('access_token');
          const storedUser = localStorage.getItem('user');

          if (storedToken && storedUser) {
            const parsedUser = JSON.parse(storedUser);
            setUser(parsedUser);
            setUserProfile(parsedUser);
            setSession({
              access_token: storedToken,
              refresh_token: localStorage.getItem('refresh_token') || '',
            });
          } else {
            await clearAuthState();
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        await clearAuthState();
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);


  const clearAuthState = async () => {
    setUser(null);
    setUserProfile(null);
    setSession(null);

    // Clear localStorage only on client side
    if (typeof window !== 'undefined') {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
    }
  };

  const login = useCallback(async (email: string, password: string) => {
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        return {
          success: false,
          message: data.message || 'Login failed',
        };
      }

      // Store tokens and user data only on client side
      if (typeof window !== 'undefined') {
        localStorage.setItem('access_token', data.session.access_token);
        localStorage.setItem('refresh_token', data.session.refresh_token);
        localStorage.setItem('user', JSON.stringify(data.user));
      }

      setUser(data.user);
      setUserProfile(data.user);
      setSession(data.session);

      return {
        success: true,
        message: 'Login successful',
      };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        message: 'An unexpected error occurred',
      };
    }
  }, []);

  const logout = useCallback(async () => {
    await clearAuthState();
    router.push('/');
  }, [router]);

  const refreshSession = useCallback(async () => {
    try {
      // Only access localStorage on client side
      if (typeof window === 'undefined') return;
      
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        await clearAuthState();
        return;
      }

      // Call refresh token endpoint
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        console.error('Token refresh failed:', data.message);
        await clearAuthState();
        return;
      }

      // Update stored tokens and user data
      localStorage.setItem('access_token', data.session.access_token);
      localStorage.setItem('refresh_token', data.session.refresh_token);
      localStorage.setItem('user', JSON.stringify(data.user));

      // Update state
      setUser(data.user);
      setUserProfile(data.user);
      setSession(data.session);
    } catch (error) {
      console.error('Error refreshing session:', error);
      await clearAuthState();
    }
  }, []);

  const refreshUserProfile = useCallback(async () => {
    try {
      // Only access localStorage on client side
      if (typeof window === 'undefined') return;
      
      const storedUser = localStorage.getItem('user');
      const accessToken = localStorage.getItem('access_token');
      
      if (!storedUser || !accessToken) {
        console.error('No user or token found in localStorage');
        return;
      }

      const user = JSON.parse(storedUser);
      
      // Fetch updated user profile from server
      const response = await fetch(`/api/user/profile?userId=${user.id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        console.error('Failed to fetch updated user profile');
        return;
      }

      const updatedProfile = await response.json();
      
      // Update localStorage with the fresh profile data
      const updatedUser = {
        ...user,
        subscription: updatedProfile.subscription,
        stripe_customer_id: updatedProfile.stripe_customer_id,
        stripe_subscription_id: updatedProfile.stripe_subscription_id,
        updated_at: updatedProfile.updated_at,
        role: updatedProfile.role,
      };

      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      // Update state
      setUser(updatedUser);
      setUserProfile(updatedUser);
      
      console.log('✅ User profile refreshed from server');
    } catch (error) {
      console.error('Error refreshing user profile:', error);
    }
  }, []);

  const checkAuth = useCallback(async (): Promise<boolean> => {
    // Only access localStorage on client side
    if (typeof window === 'undefined') return false;
    
    const token = localStorage.getItem('access_token');
    const storedUser = localStorage.getItem('user');

    if (!token || !storedUser) {
      await clearAuthState();
      return false;
    }

    // Verify token is still valid
    try {
      const parsedUser = JSON.parse(storedUser);
      const refreshToken = localStorage.getItem('refresh_token');
      
      // Update state with current user data from localStorage
      setUser(parsedUser);
      setUserProfile(parsedUser);
      setSession({
        access_token: token,
        refresh_token: refreshToken || '',
      });
      
      return true;
    } catch (error) {
      await clearAuthState();
      return false;
    }
  }, []);

  const value: AuthContextType = {
    user,
    userProfile,
    session,
    loading,
    isAuthenticated,
    login,
    logout,
    refreshSession,
    refreshUserProfile,
    checkAuth,
  };

  return <AuthContext.Provider data-component-id="src/lib/context/User.context.tsx:295:9" data-component-path="src/lib/context/User.context.tsx" data-component-line="295" data-component-file="User.context.tsx" data-component-name="AuthContext.Provider" data-component-content="%7B%22elementName%22%3A%22AuthContext.Provider%22%2C%22value%22%3A%22%5Bvar%3Avalue%5D%22%7D" value={value}>{children}</AuthContext.Provider>;
};
